import { Actions } from '../state/actions';

import { useEffect, useMemo } from 'react';
import { css, styled } from '@compiled/react';

import ReactIntl, { FormattedMessage, useIntl } from 'react-intl';
import { token } from '@atlaskit/tokens';

import VisuallyHidden from '@atlaskit/visually-hidden';

import { messages } from '@/components/jsw-interactive-roadmap/src/messages.ts';
import { useAppSelector } from '../state/hook';
import { Spotlight } from '../state/reducer';

import { Epic16IconCustom } from './epic-16-icon-custom';

type MessageBoxPropsType = {
	header: ReactIntl.MessageDescriptor;
	body: ReactIntl.MessageDescriptor;
	icon?: JSX.Element;
};
type SpotlightWrapperPropsType = {
	posX: number;
	posY: number;
};
const SpotlightWrapper = styled.div<SpotlightWrapperPropsType>({
	position: 'absolute',
	insetBlockStart: `${({ posY }: { posY: number }) => posY}px`,
	insetInlineStart: `${({ posX }: { posX: number }) => posX}px`,
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'flex-start',
	alignItems: 'flex-start',
	transition: 'opacity 0.3s ease-in',
	pointerEvents: 'none',
	zIndex: 2,
});

const baseShadow = `0 0 0 1px ${token('color.border.focused', 'rgba(29, 122, 252, 1)')}`;

const pulseBlob = css({
	backgroundColor: '#1D7AFC80',
	border: '2px solid #0C66E4',
	borderRadius: '50%',
	height: '36px',
	width: '36px',
	margin: '5px 10px 10px 10px',
	['@keyframes roadmapPulse']: {
		from: {
			boxShadow: `${baseShadow}, 0 0 0 0px ${token(
				'color.border.focused',
				'rgba(56, 139, 255, 1)',
			)}`,
		},
		to: {
			boxShadow: `${baseShadow}, 0 0 0 10px rgba(56, 139, 255, 0.01)`,
		},
	},
	animation: 'roadmapPulse 2s infinite',
});

const TextWrapper = css({
	width: '362px',
	height: 'auto',
	display: 'flex',
	flexDirection: 'column',
	padding: '16px 20px',
	borderRadius: '16px',
	border: '2px solid #0C66E4',
	backgroundColor: '#FFF',
	boxShadow: '0px 8px 12px 0px #091E4226, 0px 0px 1px 0px #091E424F',
});
const HeaderText = css({
	display: 'flex',
	flexDirection: 'row',
	gap: '10px',
	marginBlockStart: 0,
	fontSize: '20px',
	fontWeight: '700',
	marginBlockEnd: '10px',
});
const SubheaderText = css({
	color: '#000',
	fontSize: '16px',
	fontWeight: '400',

	// override <p> tag default margin
	p: {
		marginBlockStart: '0px',
	},
});

const MessageBox = ({ header, body, icon }: MessageBoxPropsType) => {
	return (
		<div css={TextWrapper}>
			<div css={HeaderText}>
				{icon && icon}
				<FormattedMessage {...header} />
			</div>
			<div css={SubheaderText}>
				<FormattedMessage {...body} />
			</div>
		</div>
	);
};

const ScreenReaderMessageBox = ({ header, body }: MessageBoxPropsType) => {
	// Unsophisticated way to prevent the aria-live screen reader announcement on first render
	const isFirstMessage = (id: string) => id.endsWith('Stage1');

	if (isFirstMessage(header.id as string) || isFirstMessage(body.id as string)) {
		return null;
	}

	return (
		<>
			<FormattedMessage {...header} /> <FormattedMessage {...body} />
		</>
	);
};

export const SpotlightView = () => {
	const {
		header,
		body,
		posX = 0,
		posY = 0,
	} = useAppSelector(({ spotlight }) => spotlight || {}) as Spotlight;
	const intl = useIntl();
	const epicLabelText = useMemo(() => intl.formatMessage(messages.epic), [intl]);

	const isDemoComplete = useAppSelector(({ isDemoComplete }) => isDemoComplete);

	/**
	 * custom event listener to detect when project timeline is adjusted.
	 * This will transition the spotlight opacity to ZERO until the next
	 * interactive state is dispatched.
	 * */
	useEffect(() => {
		addEventListener(Actions.ADJUST_PROJECT_TIMELINE, () => {
			const element = document.getElementById('spotlight-wrapper');
			if (element) {
				element.style.opacity = '0';
			}
		});

		addEventListener(`${Actions.ADJUST_PROJECT_TIMELINE}_REWIND`, () => {
			const element = document.getElementById('spotlight-wrapper');
			if (element) {
				element.style.opacity = '1';
			}
		});
	}, []);

	useEffect(() => {
		const element = document.getElementById('spotlight-wrapper');
		if (element) {
			element.style.opacity = '1';
		}
	}, [header, body, posX, posY]);

	if (isDemoComplete || header === undefined) {
		return null;
	}

	return (
		<>
			<SpotlightWrapper
				id="spotlight-wrapper"
				data-testid="spotlight-wrapper"
				posX={posX}
				posY={posY}
			>
				<div css={pulseBlob} />
				<MessageBox
					header={header}
					body={body}
					icon={<Epic16IconCustom primaryColor="#BF63F3" label={epicLabelText} />}
				/>
			</SpotlightWrapper>
			{/* Elements with the role status have an implicit aria-live value of polite and an implicit aria-atomic value of true. */}
			<VisuallyHidden role="status">
				<ScreenReaderMessageBox header={header} body={body} />
			</VisuallyHidden>
		</>
	);
};
